import React, { memo, useState, useMemo } from 'react';
import { View, Text, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import Unicon from '@/components/General/Unicon';
import SheetModal from '@/components/General/SheetModal';
import Input from '@/components/General/inputs/Input';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import GuideTarget from '@/components/Guides/GuideTarget';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { createQuickFilters, updateQuickFilters, deleteQuickFilters } from '@/data/quickFilters';
import { showAlert } from '@/utils/showAlert';

/**
 * Quick Filter Manager Component
 * Handles saving, loading, editing, and deleting quick filters
 * 
 * @param {object} props
 * @param {object} props.selectedQuickFilter - Currently selected quick filter
 * @param {Function} props.onSelectQuickFilter - Callback when a quick filter is selected
 * @param {Function} props.onClearQuickFilter - Callback to clear the selected quick filter
 * @param {Function} props.getSerializedFilters - Function to get current filters in serialized format
 * @param {string} props.filterLogic - Current filter logic (AND/OR)
 * @param {boolean} props.hasActiveFilters - Whether there are active filters
 */
const QuickFilterManager = ({
    selectedQuickFilter,
    onSelectQuickFilter,
    onClearQuickFilter,
    getSerializedFilters,
    filterLogic = 'AND',
    hasActiveFilters = false,
    guidePrefix = null,
}) => {
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const [filterName, setFilterName] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [saving, setSaving] = useState(false);
    // Drives the selector's controlled `open` when a guided tour activates it.
    const [selectorOpen, setSelectorOpen] = useState(false);

    const queryClient = useQueryClient();
    
    const { data: quickFilters = [] } = Query.quickFilters.all();
    const { data: loggedInUser } = Query.people.loggedInUser();

    // Mutations
    const createMutation = useMutation({
        mutationFn: createQuickFilters,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.quickFilters.all() });
            onSelectQuickFilter({
                id: data.id,
                name: data.name,
                filterJSON: data.filterJSON,
                logic: data.logic,
            });
            setSaving(false);
            setSaveModalOpen(false);
            setFilterName('');
            Toast.show({ type: 'success', text2: 'Quick filter created!' });
        },
        onError: () => {
            setSaving(false);
            Toast.show({ type: 'error', text2: 'Failed to create quick filter' });
        },
    });

    const updateMutation = useMutation({
        mutationFn: updateQuickFilters,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.quickFilters.all() });
            onSelectQuickFilter({
                id: data.id,
                name: data.name,
                filterJSON: data.filterJSON,
                logic: data.logic,
            });
            setSaving(false);
            setSaveModalOpen(false);
            Toast.show({ type: 'success', text2: 'Quick filter updated!' });
        },
        onError: () => {
            setSaving(false);
            Toast.show({ type: 'error', text2: 'Failed to update quick filter' });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteQuickFilters,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.quickFilters.all() });
            onClearQuickFilter();
            Toast.show({ type: 'success', text2: 'Quick filter deleted!' });
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to delete quick filter' });
        },
    });

    // Permission checks
    const canCreate = loggedInUser?.can?.('Create Quick Filters');
    const canEdit = loggedInUser?.can?.('Edit Quick Filters');
    const canDelete = loggedInUser?.can?.('Delete Quick Filters');

    // Validate filter name
    const validateName = () => {
        if (!filterName.trim()) {
            Toast.show({ type: 'error', text2: 'Please enter a name for your filter' });
            return false;
        }
        
        const nameExists = quickFilters.some(
            f => f.name.trim().toLowerCase() === filterName.trim().toLowerCase() &&
                 (!selectedQuickFilter?.id || f.id !== selectedQuickFilter.id)
        );
        
        if (nameExists) {
            Toast.show({ type: 'error', text2: 'A filter with this name already exists' });
            return false;
        }
        
        return true;
    };

    // Handle save
    const handleSave = () => {
        if (!validateName()) return;
        
        setSaving(true);
        const serializedFilters = getSerializedFilters();
        
        if (isUpdating && selectedQuickFilter?.id) {
            updateMutation.mutate({
                details: {
                    id: selectedQuickFilter.id,
                    name: filterName.trim(),
                    filterLogic,
                    filterJSON: serializedFilters,
                },
            });
        } else {
            createMutation.mutate({
                details: {
                    name: filterName.trim(),
                    filterLogic,
                    filterJSON: serializedFilters,
                },
            });
        }
    };

    // Handle delete
    const handleDelete = () => {
        if (!selectedQuickFilter?.id) return;
        
        showAlert({
            title: 'Delete Quick Filter',
            subtitle: `Are you sure you want to delete "${selectedQuickFilter.name}"?`,
            isDestructive: true,
            confirmText: 'Delete',
            onConfirm: () => {
                deleteMutation.mutate(selectedQuickFilter.id);
            },
        });
    };

    // Open save modal for new filter
    const openCreateModal = () => {
        setIsUpdating(false);
        setFilterName('');
        setSaveModalOpen(true);
    };

    // Open save modal for updating existing filter
    const openUpdateModal = () => {
        setIsUpdating(true);
        setFilterName(selectedQuickFilter?.name || '');
        setSaveModalOpen(true);
    };

    // Build menu options
    const menuOptions = useMemo(() => {
        const options = [];
        
        if (canCreate && hasActiveFilters) {
            options.push({
                name: 'Save as New Filter',
                icon: dropdownIcons.create,
                onSelect: openCreateModal,
            });
        }
        
        if (canEdit && selectedQuickFilter?.id) {
            options.push({
                name: 'Update Current Filter',
                icon: dropdownIcons.edit,
                onSelect: openUpdateModal,
            });
        }
        
        if (canDelete && selectedQuickFilter?.id) {
            options.push({
                name: 'Delete Filter',
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: handleDelete,
            });
        }
        
        return options;
    }, [canCreate, canEdit, canDelete, selectedQuickFilter, hasActiveFilters]);

    return (
        <View style={styles.container}>
            {/* Quick Filter Selector */}
            <View style={styles.selectorRow}>
                <View style={styles.selectorContainer}>
                    <GuideTarget id={guidePrefix ? `${guidePrefix}.quick` : null} activate={() => setSelectorOpen(true)} style={styles.selectorTarget}>
                    <SheetSelector
                        isNotOnSurface
                        placeholder="Select a saved filter"
                        type="quickFilters"
                        titleOnly
                        value={selectedQuickFilter}
                        open={selectorOpen}
                        onClose={() => setSelectorOpen(false)}
                        onChange={(selected) => {
                            setSelectorOpen(false);
                            if (selected?.id) {
                                const filter = quickFilters.find(f => f.id === selected.id);
                                if (filter) {
                                    onSelectQuickFilter({
                                        id: filter.id,
                                        name: filter.name,
                                        filterJSON: filter.filterJSON,
                                        logic: filter.logic,
                                    });
                                }
                            } else if (selected?.id === null || selected?.id === undefined) {
                                onClearQuickFilter();
                            }
                        }}
                   />
                    </GuideTarget>
                </View>
                
                {/* Menu Button */}
                {menuOptions.length > 0 && (
                    <GuideTarget id={guidePrefix ? `${guidePrefix}.save` : null}>
                    <DropdownMenu
                        triggerComponent={
                            <Pressable style={styles.menuButton}>
                                <Unicon name="ellipsis-horizontal" size={22} colorVariant="text" />
                            </Pressable>
                        }
                        options={menuOptions}
                    />
                    </GuideTarget>
                )}
            </View>

            {/* Selected Filter Info */}
            {selectedQuickFilter?.id && (
                <View style={styles.selectedInfo}>
                    <Unicon name="information-circle-outline" size={14} colorVariant="textSecondary" />
                    <Text style={styles.selectedInfoText}>
                        Using saved filter: {selectedQuickFilter.name}
                    </Text>
                </View>
            )}

            {/* Save Modal */}
            <SheetModal
                isOpen={saveModalOpen}
                onClose={() => setSaveModalOpen(false)}
                title={isUpdating ? 'Update Quick Filter' : 'Save Quick Filter'}
                confirmText="Save"
                loading={saving}
                onConfirm={handleSave}
            >
                <View style={styles.modalContent}>
                    <Input
                        title="Filter Name"
                        placeholder="Enter a name for this filter"
                        value={filterName}
                        maxLength={120}
                        onUpdate={setFilterName}
                    />
                    <Text style={styles.modalHint}>
                        {isUpdating 
                            ? 'This will update the existing filter with your current filter settings.'
                            : 'Save your current filter settings for quick access later.'
                        }
                    </Text>
                </View>
            </SheetModal>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        marginBottom: 16,
    },
    selectorRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    selectorContainer: {
        flex: 1,
    },
    selectorTarget: {
        flex: 1,
    },
    selector: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        _web: {
            cursor: 'pointer',
            transition: 'all 0.2s',
            _hover: {
                borderColor: colors.textSecondary,
            },
        },
    },
    selectorContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        flex: 1,
    },
    selectorText: {
        fontSize: 14,
        color: colors.text,
        fontFamily: fonts.body,
        flex: 1,
    },
    selectorActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    menuButton: {
        paddingVertical: 5,
        paddingHorizontal: 10,
        borderRadius: 8,
        _web: {
            cursor: 'pointer',
            transition: 'all 0.2s',
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    },
    selectedInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 8,
        paddingHorizontal: 4,
    },
    selectedInfoText: {
        fontSize: 12,
        color: colors.textSecondary,
        fontStyle: 'italic',
    },
    modalContent: {
        paddingVertical: 8,
    },
    modalHint: {
        fontSize: 13,
        color: colors.textSecondary,
        marginTop: 8,
        fontStyle: 'italic',
    },
}));

export default memo(QuickFilterManager);
